// constants
const animationDuration = 500;
const accent1Colour = getComputedStyle(document.documentElement).getPropertyValue('--col-accent-1-rgb').trim();
const accent2Colour = getComputedStyle(document.documentElement).getPropertyValue('--col-accent-2-rgb').trim();
const accent3Colour = getComputedStyle(document.documentElement).getPropertyValue('--col-accent-3-rgb').trim();
const accent4Colour = getComputedStyle(document.documentElement).getPropertyValue('--col-accent-4-rgb').trim();
const boundaryColour = accent1Colour;
const markerColour = accent4Colour;
const minMapWidthForAttribution = 500;

// keep track of scale controls for later unit switching
let scaleControls = [];

// helper to fire main fn when mapping lib loaded
function onMapsReady(callback) {
	if(window.maplibregl && typeof maplibregl.Map === 'function') callback();
	else {
		setTimeout(() => onMapsReady(callback), 100);
  }
}

// debounce helper
function debounce(fn, delay = 200) {
	let timer;
	return function (...args) {
		clearTimeout(timer);
		timer = setTimeout(() => fn.apply(this, args), delay);
	};
}

// calculate the bounds for a geoJson geometry
function geojsonBoundingBox(geojson) {
	let minX=Infinity, minY=Infinity, maxX=-Infinity, maxY=-Infinity;
	const coordsEach = (g, cb) => {
		const walk = (geom) => {
			const {type, coordinates} = geom;
			if(type === 'Polygon' || type === 'MultiPoint') coords(coordinates);
			else if(type === 'MultiPolygon' || type === 'LineString') coords(coordinates);
			else if(type === 'MultiLineString') coordinates.forEach(coords);
			else if(type === 'Point') cb(coordinates);
			else if(type === 'GeometryCollection') geom.geometries.forEach(walk);
			function coords(arr){ Array.isArray(arr[0]) ? arr.forEach(coords) : cb(arr); }
		};
		if(g.type === 'FeatureCollection') g.features.forEach(f => walk(f.geometry));
		else if(g.type === 'Feature') walk(g.geometry);
		else walk(g);
	};
	coordsEach(geojson, ([x,y]) => {
		if(x < minX) minX = x; if(y < minY) minY = y;
		if(x > maxX) maxX = x; if(y > maxY) maxY = y;
	});
	return new maplibregl.LngLatBounds([[minX, minY], [maxX, maxY]]);
}

// fetch and process boundary file
async function fetchBoundaryFile(boundary_file) {
	const response = await fetch(boundary_file);
	const geojson = await response.json();
	const bounds = geojsonBoundingBox(geojson);
	return {
		geojson: geojson,
		bounds: bounds
	};
}

// disable scroll zoom unless using ctrl/command
function disableMouseWheel(map) {
	map.scrollZoom.disable();
	map.getCanvas().addEventListener('wheel', (e) => {
		if (e.ctrlKey || e.metaKey) {
			map.scrollZoom.enable();
		} else {
			map.scrollZoom.disable();
		}
	});
}

// update the marker position on a minimap
function updateMinimapMarker(minimap_marker, coordinates) {
	const radians = coordinates.lat * (Math.PI / 180);
	const mercatorY = Math.log(Math.tan(Math.PI / 4 + radians / 2));
	let x = 383.154174232 + 46.837379735 * coordinates.lng;
	let y = 3615.47358187 - 2683.873050624 * mercatorY;
	x = Math.round((x / 467) * 100 * 10000) / 10000;
	y = Math.round((y / 910) * 100 * 10000) / 10000;
	minimap_marker.style.top = y+'%';
	minimap_marker.style.left = x+'%';
}

// mapping lib loaded
onMapsReady(initMaps);

function initMaps() {
	
	// iterate maps
	let map_elements = document.querySelectorAll('.map:not(.initialised)');
	map_elements.forEach(async (map_element)=>{
		
		map_element.classList.add('initialised');
	
		// error helper
		function mapError(error) {
			console.error('Map error: '+error);
			//map_element.remove();
		}
		
		// reset button
		class ResetButton {
			onAdd(map) {
				this.map = map;
				this.container = document.createElement('div');
				this.container.className = 'maplibregl-ctrl maplibregl-ctrl-group';
				this.button = document.createElement('button');
				this.button.type = 'button';
				this.button.title = 'Reset map';
				this.button.setAttribute('aria-label', 'Reset map');
				this.button.innerHTML = '<span class="maplibregl-ctrl-icon maplibregl-ctrl-reset-icon"></span>';
				this.button.addEventListener('click', initialView);
				this.container.appendChild(this.button);
				return this.container;
			}
			onRemove() {
				this.container.remove();
				this.map = undefined;
				this.button = undefined;
			}
		}
		const reset_control = new ResetButton();
		
		// get map data
		let data = null;
		const data_element = map_element?.querySelector('.mapdata');
		const json = data_element?.textContent.trim();
		if(json) {
			try {
				data = JSON.parse(json);
			} catch (error) {
				mapError('Invalid map data JSON');
			}
		}
		if(!data || typeof data !== 'object' || Array.isArray(data)) {
			mapError('Source data not found or invalid');
			data = {};
		}
		
		// defaults
		data.coordinates ??= {latitude:54.25, longitude:-4.25};
		data.bounds ??= null;
		data.bounds_padding ??= 40;
		data.boundary_files ??= [];
		data.min_zoom ??= 3;
		data.max_zoom ??= 18;
		data.max_bounds_zoom ??= 16;
		data.default_zoom ??= 4;
		data.nav_controls ??= true;
		data.disable_mousewheel ??= true;
		data.reset_button ??= true;
		data.reset_on_resize ??= true;
		data.markers ??= [];
		data.fit_markers ??= false;
		data.scale ??= false;
		
		// map must have coordinates
		if(!data.coordinates) {
			mapError ('No coordinates found');
			return;
		}
		
		// does map have a minimap?
		const minimap = map_element.parentElement.querySelector('.mini-map');
		
		// create map
		const map = new maplibregl.Map({
			container: map_element,
			style: '/assets/map_styles/bright_no_poi.json',
			center: [data.coordinates.longitude, data.coordinates.latitude],
			zoom: data.default_zoom,
			minZoom: data.min_zoom,
			maxZoom: data.max_zoom,
			cooperativeGestures: true
		});
		
		let calculated_bounds = null;
		let boundaries_loaded = false;
		let markers_loaded = false;
		
		// disable scroll zoom unless using ctrl/command - not needed as done by cooperativeGestures
		//if(data.disable_mousewheel) disableMouseWheel(map);
		
		// add nav control
		if(data.nav_controls) map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
		
		// add scale control
		if(data.scale) {
			let unit = document.body.dataset.distanceUnits === 'km' ? 'metric' : 'imperial';
			const scaleControl = new maplibregl.ScaleControl({ maxWidth: 120, unit: unit});
			scaleControls.push(scaleControl);
			map.addControl(scaleControl, 'bottom-left');		
		}
		
		// add reset button
		if(data.reset_button) {
			map.addControl(reset_control, 'top-right');
			map.on('movestart', () => {
				reset_control.button.disabled = false;
			});
		}
		
		// distance map
		if((data.type === 'distance') && (data.markers.length === 2)) {
			map.on('load', () => {
				map.addSource('dist-line', {
					type: 'geojson',
					data: {
						type: 'Feature',
						geometry: {
							type: 'LineString',
							coordinates: [
								[data.markers[0].coordinates.longitude, data.markers[0].coordinates.latitude],
								[data.markers[1].coordinates.longitude, data.markers[1].coordinates.latitude]
							]
						}
					}
				});
				map.addLayer({
					id: 'dist-line',
					type: 'line',
					source: 'dist-line',
					layout: {
						'line-join': 'round',
						'line-cap': 'round'
					},
					paint: {
						'line-color': accent3Colour,
						'line-width': 3
					}
				});
				if(data.route && data.route.geometry) {
					map.addSource('dist-route', {
						type: 'geojson',
						data: {
							type: 'Feature',
							geometry: data.route.geometry
						}
					});
					map.addLayer({
						id: 'dist-route',
						type: 'line',
						source: 'dist-route',
						paint: {
							'line-color': accent4Colour,
							'line-width': 3
						}
					})
				}
			});
		}
		
		// boundary outlines
		if(data.boundary_files.length > 0) {
			const run = async () => {
				const boundaries = await Promise.all(
					data.boundary_files
						.filter(item => item.file !== null)
						.map(async item => ({
							...item,
							...(await fetchBoundaryFile(item.file))
					}))
				);
				boundaries.forEach((boundary, i) => {	
					
					if(!boundary.style) boundary.style = 'default';
					
					if(boundary.extend_bounds) {
						if(calculated_bounds === null) calculated_bounds = new maplibregl.LngLatBounds();
						calculated_bounds.extend(boundary.bounds);		
					}
					
					map.addSource('boundary-'+i, {
						type: 'geojson',
						data: boundary.geojson
					});
					
					if(boundary.style === 'park') {
						map.addLayer({
							id: 'boundary-outline-layer-'+i,
							type: 'line',
							source: 'boundary-'+i,
							paint: {
								'line-color': boundaryColour,
								'line-width': 2,
								'line-opacity': 0.67
							}
						});
						map.addLayer({
							id: 'boundary-layer-'+i,
							type: 'fill',
							source: 'boundary-'+i,
							paint: {
								'fill-color': boundaryColour,
								'fill-opacity': 0.33
							}
						});
					}
					
					else if(boundary.style === 'country') {
						let colours = [accent1Colour, accent2Colour, accent3Colour, accent4Colour];
						let colourKey = ((i+3) % 4);
						map.addLayer({
							id: 'boundary-outline-layer-'+i,
							type: 'line',
							source: 'boundary-'+i,
							paint: {
								'line-color': colours[colourKey],
								'line-width': 2,
								'line-opacity': 0.67
							}
						});
						map.addLayer({
							id: 'boundary-layer-'+i,
							type: 'fill',
							source: 'boundary-'+i,
							paint: {
								'fill-color': colours[colourKey],
								'fill-opacity': 0.33
							}
						});
					}
					
					// default
					else {
						map.addLayer({
							id: 'boundary-layer-'+i,
							type: 'line',
							source: 'boundary-'+i,
							paint: {
								'line-color': boundaryColour,
								'line-width': 2,
								'line-opacity': 0.67
							}
						});
					}
					
					if(boundary.popup_content) {
						map.on('click', 'boundary-layer-' + i, (e) => {
							const coordinates = boundary.bounds.getCenter();
							new maplibregl.Popup({
								offset: -40,
								anchor: 'bottom'
							})
							.setLngLat(coordinates)
							.setHTML(boundary.popup_content)
							.addTo(map);
							map.fitBounds(boundary.bounds, {
								padding: data.bounds_padding,
								maxZoom: data.max_bounds_zoom,
								duration: animationDuration
							});
						});
						map.on('mouseenter', 'boundary-layer-' + i, () => {
							map.getCanvas().style.cursor = 'pointer';
						});
						map.on('mouseleave', 'boundary-layer-' + i, () => {
							map.getCanvas().style.cursor = '';
						});
					}
					
				});
				initialView(true);
				boundaries_loaded = true;
			};
		
			if(map.loaded()) run();
			else map.once('load', run);
		}
		else boundaries_loaded = true;
		
		// markers
		if(data.markers.length > 0) {
		
			// create geojson
			let marker_points = [];
			for(let i = 0; i < data.markers.length; i++) {
				let m = data.markers[i];
				marker_points.push({ id: i, popup_content: m.popup_content ?? null, lat: m.coordinates.latitude, lng: m.coordinates.longitude});
			}
			const marker_geojson = {
				type: 'FeatureCollection',
				features: marker_points.map(point => ({
					type: 'Feature',
					properties: {
						id: point.id,
						popup_content: point.popup_content
					},
					geometry: {
						type: 'Point',
						coordinates: [point.lng, point.lat]
					}
				}))
			};
			
			// fit bounds to markers
			if(data.fit_markers) {
				calculated_bounds = new maplibregl.LngLatBounds();
				marker_geojson.features.forEach(feature => {
					calculated_bounds.extend(feature.geometry.coordinates);
				});
				data.bounds_padding = 60;
			}
			
			map.on('load', async()=>{
				
				const pins = [
					{
						name: 'pin',
						svg: '<svg xmlns="http://www.w3.org/2000/svg" width="56" height="80" viewBox="0 0 56 80"><path d="M28 0C12.6 0 0 12.6 0 28c0 21 28 52 28 52s28-31 28-52C56 12.6 43.4 0 28 0z" fill="'+markerColour+'"/><circle cx="28" cy="28" r="12" fill="#fff"/></svg>'
					},
					{
						name: 'clusterpin',
						svg: '<svg xmlns="http://www.w3.org/2000/svg" width="56" height="80" viewBox="0 0 56 80"><path d="M28 0C12.6 0 0 12.6 0 28c0 21 28 52 28 52s28-31 28-52C56 12.6 43.4 0 28 0z" fill="'+markerColour+'"/></svg>'
					}
				];
				pins.forEach(pin => {
					const img = new Image();
					img.onload = ()=>{
						if(!map.hasImage(pin.name)) map.addImage(pin.name, img);
					};
					img.src = 'data:image/svg+xml;charset=utf-8,'+encodeURIComponent(pin.svg);
				});
				
				// add source
				map.addSource('places', {
					type: 'geojson',
					data: marker_geojson,
					cluster: true,
					clusterMaxZoom: 14,
					clusterRadius: 50
				});
				
				// clusters
				map.addLayer({
					id: 'clusters',
					type: 'symbol',
					source: 'places',
					filter: ['has', 'point_count'],
					layout: {
						'icon-image': 'clusterpin',
						'icon-size': .5,
						'icon-anchor': 'bottom',
						'icon-allow-overlap': true,
						'icon-ignore-placement': true,
						'text-field': ['get', 'point_count_abbreviated'],
						'text-font': ['Noto Sans Bold'],
						'text-size': 13,
						'text-anchor': 'center',
						'text-offset': [0, -1.95],
						'text-allow-overlap': true
					},
					paint: {
						'text-color': '#fff'
					}
				});
				
				// points
				map.addLayer({
					id: 'unclustered-point',
					type: 'symbol',
					source: 'places',
					filter: ['!', ['has', 'point_count']],
					layout: {
						'icon-image': 'pin',
						'icon-size': .5,
						'icon-anchor': 'bottom',
						'icon-allow-overlap': true,
						'icon-ignore-placement': true,
					}
				});
				
				// click a cluster: zoom to its expansion zoom
				map.on('click', 'clusters', async (e) => {
					const features = map.queryRenderedFeatures(e.point, {
						layers: ['clusters']
					});
				
					if (!features.length) return;
				
					const clusterId = features[0].properties.cluster_id;
					const source = map.getSource('places');
					const zoom = await source.getClusterExpansionZoom(clusterId);
				
					map.easeTo({
						center: features[0].geometry.coordinates,
						zoom,
						duration: animationDuration
					});
				});
				
				// click an individual point: show a popup
				map.on('click', 'unclustered-point', (e) => {
					const feature = e.features[0];
					const popup_content = feature.properties.popup_content;
					if(popup_content) {
						const coordinates = feature.geometry.coordinates.slice();
						new maplibregl.Popup({
							offset: 50,
						    anchor: 'bottom'
						})
						.setLngLat(coordinates)
						.setHTML(popup_content)
						.addTo(map);
						
						map.easeTo({
							center: coordinates,
							offset: [0, 80],   // push map down so popup fits
							duration: animationDuration
						});
					}
				});
				
				// cursor feedback
				map.on('mouseenter', 'clusters', () => {
					map.getCanvas().style.cursor = 'pointer';
				});
				map.on('mouseleave', 'clusters', () => {
					map.getCanvas().style.cursor = '';
				});
				
				map.on('mouseenter', 'unclustered-point', (e) => {
					const feature = e.features[0];
					const popup_content = feature.properties.popup_content;
					if(popup_content) map.getCanvas().style.cursor = 'pointer';
				});
				map.on('mouseleave', 'unclustered-point', () => {
					map.getCanvas().style.cursor = '';
				});
				
				markers_loaded = true;
				
			});
		}
		else markers_loaded = true;
		
		// use bounds in data if not overruled by boundary file bounds or marker bounds
		if(!calculated_bounds && data.bounds) {
			calculated_bounds = new maplibregl.LngLatBounds([[data.bounds.sw.longitude, data.bounds.sw.latitude], [data.bounds.ne.longitude, data.bounds.ne.latitude]]);
		}
		
		// move minimap when this map view changes
		if(minimap) {
			const minimap_marker = minimap.querySelector('.marker');
			if(minimap_marker) {
				map.on('move', () => {
					updateMinimapMarker(minimap_marker, map.getCenter());
				});
			}
		}
		
		// initial view
		function initialView(snap){
	
			const duration = (snap === true) ? 0 : animationDuration;
				
			// fit to bounds if set
			if(calculated_bounds) {
				map.fitBounds(calculated_bounds, {
					padding: data.bounds_padding,
					maxZoom: data.max_bounds_zoom,
					duration: duration
				});
			}
			
			// otherwise just centre and reset zoom
			else {
				map.easeTo({
					center: [data.coordinates.longitude, data.coordinates.latitude],
					zoom: data.default_zoom,
					duration: duration
				});
			}
			
			// expand / minimise attribution
			const attrib = map.getContainer().querySelector('.maplibregl-ctrl-attrib');
			if(attrib) {
				if(map.getContainer().clientWidth < minMapWidthForAttribution) {
					attrib.classList.remove('maplibregl-compact-show');
				} else {
					attrib.classList.add('maplibregl-compact-show');
				}
			}
			
			// disable reset button
			if(data.reset_button && reset_control?.button) reset_control.button.disabled = true;
			
			// remove any popups
			map_element.querySelectorAll('.maplibregl-popup').forEach(p => p.remove());
			
		}
		initialView(true);
		
		// reset view on resize
		if(data.reset_on_resize) {
			map.on('resize', debounce(() => {
				initialView();
			}, 250));
		}
		
		// add loaded class once everything is done
		function everythingLoaded() {
			//console.log('tick');
			if(map.loaded() && boundaries_loaded && markers_loaded) {
				map_element.classList.add('loaded');
			}
			else setTimeout(everythingLoaded, 100);
		}
		everythingLoaded();
		
		
	});
	
}
  